import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { resolveSeoData, siteOrigin } from "@/seo/resolve";
import { applyJsonLd, clearJsonLd } from "@/seo/jsonLd";

type MetaAttr = "name" | "property";

function ensureMeta(attr: MetaAttr, key: string): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(attr: MetaAttr, key: string, content: string): void {
  ensureMeta(attr, key).setAttribute("content", content);
}

function removeMeta(attr: MetaAttr, key: string): void {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (el) el.remove();
}

function setCanonical(href: string): void {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const pathname = location.pathname;
    const data = resolveSeoData(pathname);
    const origin = siteOrigin();

    document.title = data.title;

    setMeta("name", "description", data.description);
    setMeta("name", "keywords", data.keywords);
    setMeta("name", "robots", data.robots || "index, follow");

    const canonical = origin ? `${origin}${data.canonicalPath}` : data.canonicalPath;
    setCanonical(canonical);

    setMeta("property", "og:title", data.title);
    setMeta("property", "og:description", data.description);
    setMeta("property", "og:type", data.ogType);
    setMeta("property", "og:url", canonical);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", data.title);
    setMeta("name", "twitter:description", data.description);

    if (data.ogImage) {
      setMeta("property", "og:image", data.ogImage);
      setMeta("name", "twitter:image", data.ogImage);
    } else {
      removeMeta("property", "og:image");
      removeMeta("name", "twitter:image");
    }

    if (data.jsonLd && data.jsonLd.length > 0) {
      applyJsonLd(data.jsonLd);
    } else {
      clearJsonLd();
    }
  }, [location.pathname]);

  return null;
}